import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

interface WarehousesPlanFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    schema: any;
    storages: any[];
    targetForDelete: any[];

    meta: any;
    acidity_ranges: any[];
}

const state: WarehousesPlanFormState = {
    loading: false,
    loaded: true,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    form: null,
    schema: null,

    meta: {},
    acidity_ranges: [
        {id: 4, range: [10.0, 5.16], color: '#FF0000'},
        {id: 3, range: [5.17, 5.15], color: '#FFAA00'},
        {id: 2, range: [5.14, 5.11], color: '#FFFF00'},
        {id: 1, range: [5.10, 4.97], color: '#00FF00'},
        {id: 2, range: [4.96, 4.93], color: '#FFFF00'},
        {id: 3, range: [4.92, 4.90], color: '#FFAA00'},
        {id: 4, range: [4.89, 0.90], color: '#FF0000'},
    ]
};

const initState: WarehousesPlanFormState = cloneDeep(state);

export const BASES_MANAGER_DATA = 'GET_BASES_MANAGER_DATA';
export const BASES_MANAGER_SCHEMA = 'GET_BASES_MANAGER_SCHEMA';
export const BASES_MANAGER_STORAGES = 'BASES_MANAGER_STORAGES';

export const actions: ActionTree<WarehousesPlanFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('MARK_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('MARK_DATA_SAVE')
                    .create('s:shift_atom_coock_plan_marks', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('MARK_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    addItem({ state }, {storage_id}) {
        state.storages.push({storage_id: storage_id})
    },

    removeItem({ state }, index: number) {
        if (state.storages[index].id) {
            state.targetForDelete.push(state.storages[index].id)
        }

        state.storages.splice(index, 1);
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    changeStorageValue({ state }, { index, field, newValue}) {
        const _sum = state.storages.reduce((prev: any, item: any, idx) => {
            return prev + (idx == index ? 0 : parseInt(item.quantity))
        }, 0)
        switch (field) {
            case 'quantity':
                newValue = parseInt(newValue)
                if (_sum + newValue > state.form.quantity) {
                    newValue = state.form.quantity - _sum;
                }
                state.storages[index][field] = newValue;
                break

            default:
                state.storages[index][field] = newValue;
        }
    },

    addAttachments({state, dispatch}, {newValue}) {
      state.form.attachments = [
        newValue,
        ...(state.form.attachments ? state.form.attachments : []),
      ]
    },

};

export const mutations: MutationTree<WarehousesPlanFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_BASES_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema, params}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        if (params.documentPreview && params.documentPreview.state) {
            state.form.state = params.documentPreview.state
        }
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
        
        // Инициализируем поле для выбора склада назначения
        if (!state.form.target_warehouse_id) {
            state.form.target_warehouse_id = null;
        }
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: WarehousesPlanFormState, {field, newValue}) {
        const schema_field = state.schema.fields.filter((item: any) => {
            return item.code == field
        })[0]

        const calc = () => {
            const allow = ['acidity_1', 'acidity_4', 'middle'].map((key: string) => {
                return state.form[key]
            }).filter((item: any) => {
                return !item
            })

            if (!allow.length) {
                const target = state.acidity_ranges.filter((item: any) => {
                    return state.form['acidity_4'] > item['range'][1] && state.form['acidity_4'] < item['range'][0]
                })[0].id
                state.form['total'] = target == 1 ? state.form['middle'] : Math.max(target, state.form['middle'])
            }
        }


        switch (field) {
            case 'acidity_1':
            case 'acidity_2':
            case 'acidity_3':
            case 'acidity_4':
                state.form[field] = newValue;
                calc()
                break;

            case 'structure':
            case 'taste':
                state.form[field] = newValue;
                if (state.form['structure'] && state.form['taste']) {
                    state.form['middle'] = Math.max(state.form['structure'], state.form['taste'])
                }
                calc()
                break;

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: WarehousesPlanFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_warehouses_plan_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
